import { axiosCompanyPortalInstance } from "./";

/** JSON:API single resource `{ id, type, attributes }` → flat object for session storage. */
export const flattenCompanyPortalResource = (node) => {
  if (!node || typeof node !== "object") return node;
  const attrs = node.attributes;
  if (attrs && typeof attrs === "object") {
    return {
      ...attrs,
      id: node.id ?? attrs.id,
      type: node.type,
    };
  }
  return node;
};

const pickData = (res) => {
  const raw = res?.data?.data ?? res?.data;
  if (Array.isArray(raw)) {
    return raw.map(flattenCompanyPortalResource);
  }
  return flattenCompanyPortalResource(raw);
};

const pickListData = (res) => {
  const raw = res?.data?.data ?? res?.data;
  if (Array.isArray(raw)) {
    return raw.map(flattenCompanyPortalResource);
  }
  if (raw == null || raw === "") return [];
  const one = flattenCompanyPortalResource(raw);
  return one ? [one] : [];
};

/** Backend may respond HTTP 200 with `{ success: false, message }` — treat as failure for callers. */
const throwIfCompanyPortalBodyFailed = (response) => {
  const body = response?.data;
  if (!body || body.success !== false) return;
  let msg = body.message ?? body.error ?? "";
  if (Array.isArray(msg)) {
    msg = msg.filter(Boolean).join(", ");
  }
  if (typeof msg !== "string") {
    msg = String(msg || "");
  }
  const trimmed = msg.trim() || "Request failed";
  const err = new Error(trimmed);
  err.response = { status: response.status, data: body };
  throw err;
};

/**
 * GET `/cities` on the company portal.
 * `city_id` on POST /companies must be a **city** id (cities table), not a country id from `/countries`.
 * Pass `name` only when searching (`name` omitted when empty).
 */
export const fetchCompanyPortalCities = async (params = {}) => {
  const query = { ...params };
  if (query.name == null || String(query.name).trim() === "") {
    delete query.name;
  } else {
    query.name = String(query.name).trim();
  }
  const response = await axiosCompanyPortalInstance.get("/cities", {
    params: query,
  });
  return pickListData(response);
};

/**
 * POST /company_users — create portal user (unverified).
 * Full URL: {origin}/company_portal/v1/company_users (no `/api` prefix on host).
 */
export const createCompanyUser = async (payload) => {
  const response = await axiosCompanyPortalInstance.post(
    "/company_users",
    payload
  );
  return pickData(response);
};

/**
 * POST /company_users/verify_email — requires Bearer token.
 */
export const verifyCompanyUserEmail = async (payload) => {
  const response = await axiosCompanyPortalInstance.post(
    "/company_users/verify_email",
    payload,
    { handlerEnabled: false }
  );
  throwIfCompanyPortalBodyFailed(response);
  return pickData(response);
};

/**
 * POST /company_users/resend_email_verification — Bearer token, no body.
 */
export const resendCompanyUserEmailVerification = async () => {
  const response = await axiosCompanyPortalInstance.post(
    "/company_users/resend_email_verification",
    undefined,
    { handlerEnabled: false }
  );
  throwIfCompanyPortalBodyFailed(response);
  return pickData(response);
};

/**
 * POST /companies — requires Bearer token.
 */
export const createCompany = async (payload) => {
  const response = await axiosCompanyPortalInstance.post("/companies", payload);
  return pickData(response);
};

/**
 * GET /companies/my_company — fresh company profile for the logged-in owner.
 * Expected to also expose the currently-active `company_edit_request` (when
 * one exists) so the FE can recover from a page refresh without hitting the
 * BE's "already has an active edit request" guard.
 */
export const fetchMyCompany = async () => {
  const response = await axiosCompanyPortalInstance.get("/companies/my_company");
  throwIfCompanyPortalBodyFailed(response);
  return pickData(response);
};

/**
 * GET /company_users/me — current portal user (Bearer). Used to refresh `company.status`
 * (e.g. leave pending-approval when approved).
 */
export const fetchCompanyPortalCurrentUser = async () => {
  const response = await axiosCompanyPortalInstance.get("/company_users/me");
  throwIfCompanyPortalBodyFailed(response);
  const user = pickData(response);
  const next = { ...user };
  if (next.company) {
    next.company = flattenCompanyPortalResource(next.company);
  }
  if (next.company_role) {
    next.company_role = flattenCompanyPortalResource(next.company_role);
  }
  return next;
};
